import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getFounderData, FounderData } from '../services/founderService';

const AboutPage: React.FC = () => {
  const [founderData, setFounderData] = useState<FounderData>({
    name: 'Marcus Veltrix',
    picture: '/images/loom.jpeg',
    title: 'Founder & CEO',
    bio: `Marcus Veltrix founded Veltrix Apparel in Manhattan with a simple mission: to give men one destination for premium suits, football jerseys, and training kits — without compromise on fit, quality, or style.

After years in menswear and sportswear on both sides of the Atlantic, Marcus saw a gap: guys who wanted a sharp suit for Monday and a proper jersey for the weekend had to chase multiple brands. In 2015 he opened the first Veltrix Apparel showroom in Manhattan, combining tailored formal wear with performance sportswear and custom team kit under one roof.

Today Veltrix Apparel serves professionals, teams, and everyday guys who care about how they look and perform. From boardroom-ready suits and blazers to match-day jerseys and training kits, every piece is selected or built to meet the standards of the modern man.

Marcus and the team are based in Manhattan, where they offer fittings, custom orders, and bulk team kit. Whether you need one perfect suit or a full squad in your colors, Veltrix Apparel is built for you.`
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        const data = await getFounderData();
        if (data) setFounderData(data);
      } catch (error) {
        console.error('Error loading founder data:', error);
        const savedData = localStorage.getItem('founder_data');
        if (savedData) {
          try {
            setFounderData(JSON.parse(savedData));
          } catch (e) {
            console.error('Error parsing localStorage data:', e);
          }
        }
      }
    };
    loadData();
  }, []);

  return (
    <div className="min-h-screen bg-[#fafafa]">
      {/* Hero */}
      <section className="pt-28 pb-16 lg:pt-36 lg:pb-24 bg-slate-900 relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/images/collection/suits.jpg"
            alt="Veltrix Apparel"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-slate-900/75" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/55" />
        </div>
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center gap-2 text-sm text-white/80 mb-10 drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span className="text-white/60">/</span>
            <span className="text-white">About</span>
          </nav>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold text-white tracking-tight max-w-3xl drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]">
            Our story
          </h1>
          <p className="mt-6 text-lg text-white max-w-2xl drop-shadow-[0_1px_4px_rgba(0,0,0,0.55)]">
            Based in Manhattan, Veltrix Apparel is where men get suited, kitted, and ready — from the boardroom to the pitch.
          </p>
          <div className="mt-12 flex flex-wrap gap-8">
            <div>
              <div className="font-display text-2xl font-semibold text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.55)]">NYC</div>
              <div className="text-sm text-white/80 mt-0.5 drop-shadow-[0_1px_2px_rgba(0,0,0,0.7)]">Manhattan, New York</div>
            </div>
            <div>
              <div className="font-display text-2xl font-semibold text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.55)]">Suits · Jerseys · Kits</div>
              <div className="text-sm text-white/80 mt-0.5 drop-shadow-[0_1px_2px_rgba(0,0,0,0.7)]">One destination</div>
            </div>
            <div>
              <div className="font-display text-2xl font-semibold text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.55)]">Fit first</div>
              <div className="text-sm text-white/80 mt-0.5 drop-shadow-[0_1px_2px_rgba(0,0,0,0.7)]">Tailored & team ready</div>
            </div>
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
            <div>
              <h2 className="font-display text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight">
                The Veltrix story
              </h2>
              <div className="mt-8 space-y-6 text-slate-600 leading-relaxed">
                <p>
                  Veltrix Apparel started in Manhattan with a clear idea: men shouldn't have to choose between looking sharp in a suit and looking the part on the pitch. We brought suits, football jerseys, and training kits under one roof — with a focus on fit, quality, and no fluff.
                </p>
                <div className="my-8">
                  <img
                    src="/images/collection/formal.webp"
                    alt="Veltrix Apparel formal wear"
                    className="w-full aspect-video object-cover rounded-2xl"
                  />
                </div>
                <p>
                  From our showroom in New York we serve professionals who need a perfect suit, teams who need custom jerseys and kits, and everyday guys who want one place to shop for formal and sport. Every piece is chosen or made to meet the standards of the modern man.
                </p>
                <p>
                  Today we're still based in Manhattan — fitting suits, printing jerseys, and kitting out squads. Whether you need one blazer or a full team order, Veltrix Apparel is built for you.
                </p>
              </div>
            </div>
            <div className="relative">
              <img
                src="/images/collection/jersey.webp"
                alt="Veltrix Apparel jerseys"
                className="w-full aspect-[4/5] object-cover rounded-2xl"
              />
              <div className="absolute -bottom-4 -right-4 lg:bottom-0 lg:right-0 bg-slate-900 text-white px-5 py-4 rounded-xl shadow-lg">
                <div className="font-display font-semibold">Award-winning</div>
                <div className="text-sm text-slate-300 mt-0.5">Design & craftsmanship</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What we stand for */}
      <section className="py-20 lg:py-28 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <h2 className="font-display text-3xl md:text-4xl font-semibold text-slate-900 tracking-tight">
              What we stand for
            </h2>
            <p className="mt-3 text-slate-600">
              Fit, quality, and no-nonsense style — from suit to kit.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 border border-slate-100">
              <div className="w-12 h-12 rounded-xl bg-slate-900 text-white flex items-center justify-center text-xl">🤵</div>
              <h3 className="mt-5 font-display font-semibold text-slate-900 text-lg">Tailored fit</h3>
              <p className="mt-3 text-slate-600 text-sm leading-relaxed">
                Suits and formal wear cut for the modern man. We focus on fit and construction so you look sharp when it matters.
              </p>
              <img src="/images/collection/suits.jpg" alt="Suits and tailored wear" className="mt-6 w-full aspect-video object-cover rounded-xl" />
            </div>
            <div className="bg-white rounded-2xl p-8 border border-slate-100">
              <div className="w-12 h-12 rounded-xl bg-slate-900 text-white flex items-center justify-center text-xl">⚽</div>
              <h3 className="mt-5 font-display font-semibold text-slate-900 text-lg">Jerseys & kits</h3>
              <p className="mt-3 text-slate-600 text-sm leading-relaxed">
                Football jerseys and training kits built for match day and the gym. Custom team orders and fan wear available.
              </p>
              <img src="/images/collection/jersey.webp" alt="Jerseys and kits" className="mt-6 w-full aspect-video object-cover rounded-xl" />
            </div>
            <div className="bg-white rounded-2xl p-8 border border-slate-100">
              <div className="w-12 h-12 rounded-xl bg-slate-900 text-white flex items-center justify-center text-xl">📍</div>
              <h3 className="mt-5 font-display font-semibold text-slate-900 text-lg">Manhattan HQ</h3>
              <p className="mt-3 text-slate-600 text-sm leading-relaxed">
                Based in New York, we offer in-store fittings, custom orders, and bulk team kit. One destination for suits, jerseys, and kits.
              </p>
              <img src="/images/collection/gear.jpg" alt="Training gear" className="mt-6 w-full aspect-video object-cover rounded-xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Founder */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="font-display text-3xl md:text-4xl font-semibold text-slate-900 tracking-tight">
              Meet our founder
            </h2>
            <p className="mt-2 text-slate-600">The founder behind Veltrix Apparel</p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="relative">
              <img
                src={founderData.picture}
                alt={`${founderData.name}, ${founderData.title} of Veltrix Apparel`}
                className="w-full aspect-[4/5] object-cover rounded-2xl"
                onError={(e) => { (e.target as HTMLImageElement).src = '/images/loom.jpeg'; }}
              />
              <div className="absolute -bottom-4 -right-4 bg-slate-900 text-white px-5 py-4 rounded-xl shadow-lg">
                <div className="font-display font-semibold">Industry leader</div>
                <div className="text-sm text-slate-300 mt-0.5">20+ years experience</div>
              </div>
            </div>
            <div>
              <h3 className="font-display text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight">
                {founderData.name}
              </h3>
              <p className="mt-2 text-slate-600 font-medium">{founderData.title}</p>
              <div className="mt-8 space-y-4 text-slate-600 leading-relaxed">
                {founderData.bio.split('\n\n').map((paragraph, index) =>
                  paragraph.trim() ? <p key={index}>{paragraph.trim()}</p> : null
                )}
              </div>
              <div className="mt-10 grid grid-cols-2 gap-4">
                {[
                  { value: '25+', label: 'Industry awards' },
                  { value: '15+', label: 'Patents filed' },
                  { value: '50+', label: 'Countries served' },
                  { value: '100%', label: 'Quality focus' },
                ].map((stat, i) => (
                  <div key={i} className="p-4 rounded-xl bg-slate-50 border border-slate-100">
                    <div className="font-display font-semibold text-slate-900">{stat.value}</div>
                    <div className="text-sm text-slate-600 mt-0.5">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
